import React, { useEffect, useState, useMemo } from "react";
import { Box, Grid, Typography, Switch, Divider, InputLabel,  useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

import FormInput from "../../inputs/FormInput";
import LanguageToggle from "../../global/LanguageToggle";
import useCurrency from "../../../hooks/common/useCurrency";

import { useCreateShippingMethod } from "../../../hooks/shipping/useCreateShippingMethod";
import { useUpdateShippingMethod } from "../../../hooks/shipping/useUpdateShippingMethod";
import { useGetShippingZones } from "../../../hooks/shipping/useGetShippingZones";

const TYPE_OPTIONS = [
    { id: 'flat',         name: { en: 'Flat',         ar: 'سعر ثابت' } },
    { id: 'weight_based', name: { en: 'Weight-based', ar: 'حسب الوزن' } },
    { id: 'free',         name: { en: 'Free',         ar: 'مجاني' } },
    { id: 'tiered',       name: { en: 'Tiered',       ar: 'متدرج' } },
];

const FormAddEditShippingMethod = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();
    const { currency } = useCurrency();

    const { zones } = useGetShippingZones({ per_page: 200 });

    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [type, setType] = useState("flat");
    const [price, setPrice] = useState("");
    const [zoneId, setZoneId] = useState("");
    const [minWeight, setMinWeight] = useState("");
    const [maxWeight, setMaxWeight] = useState("");
    const [isActive, setIsActive] = useState(true);

    const { createShippingMethodMutate, isCreating } = useCreateShippingMethod();
    const { updateShippingMethodMutate, isUpdating } = useUpdateShippingMethod();
    const loadingSubmit = isCreating || isUpdating;

    useEffect(() => {
        if (isEdit && currentData) {
            setNameAr(currentData?.name?.ar || "");
            setNameEn(currentData?.name?.en || "");
            setType(currentData?.type || "flat");
            setPrice(currentData?.price ?? "");
            setZoneId(currentData?.zone_id ?? currentData?.zone?.id ?? "");
            setMinWeight(currentData?.min_weight_grams ?? "");
            setMaxWeight(currentData?.max_weight_grams ?? "");
            setIsActive(currentData?.is_active ?? true);
        }
    }, [isEdit, currentData]);

    const zoneOptions = useMemo(
        () => zones.map((z) => ({ id: z.id, name: z.name || { en: z.name, ar: z.name } })),
        [zones]
    );

    const isFree = type === 'free';

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            zone_id: zoneId || null,
            name: {
                en: nameEn || nameAr,
                ar: nameAr || nameEn,
            },
            type,
            price: isFree ? 0 : (price !== "" ? parseFloat(price) : 0),
            min_weight_grams: minWeight !== "" ? parseInt(minWeight, 10) : null,
            max_weight_grams: maxWeight !== "" ? parseInt(maxWeight, 10) : null,
            is_active: isActive,
        };

        const onDone = { onSuccess: () => onSubmitSuccess && onSubmitSuccess() };

        if (isEdit && currentData?.id) {
            updateShippingMethodMutate({ id: currentData.id, data: payload }, onDone);
        } else {
            createShippingMethodMutate(payload, onDone);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6" fontWeight={700}>
                    {isEdit
                        ? (isArabic ? 'تعديل طريقة شحن' : 'Edit Shipping Method')
                        : (isArabic ? 'إضافة طريقة شحن' : 'Add Shipping Method')}
                </Typography>
                <LanguageToggle activeLang={activeLang} onChange={setActiveLang} showLabel />
            </Box>

            <Divider />

            <Grid container spacing={3}>
                {activeLang === 'ar' && (
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label="الاسم (عربي)"
                            placeholder="مثال: شحن سريع"
                            type="text"
                            value={nameAr}
                            onChange={(e) => setNameAr(e.target.value)}
                            required
                        />
                    </Grid>
                )}
                {activeLang === 'en' && (
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label="Name (English)"
                            placeholder="e.g: Express Shipping"
                            type="text"
                            value={nameEn}
                            onChange={(e) => setNameEn(e.target.value)}
                            required
                        />
                    </Grid>
                )}

                <Grid item xs={12} md={6}>
                    <FormInput
                        label={isArabic ? 'النوع' : 'Type'}
                        type="select"
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        options={TYPE_OPTIONS}
                        required
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormInput
                        label={isArabic ? 'المنطقة' : 'Zone'}
                        type="select"
                        value={zoneId}
                        onChange={(e) => setZoneId(e.target.value)}
                        options={zoneOptions}
                        required={false}
                    />
                </Grid>

                {!isFree && (
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'السعر' : 'Price'}
                            type="number"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            InputProps={{ startAdornment: currency }}
                            required
                        />
                    </Grid>
                )}

                <Grid item xs={12} md={6}>
                    <FormInput
                        label={isArabic ? 'أدنى وزن (جم)' : 'Min Weight (g)'}
                        type="number"
                        value={minWeight}
                        onChange={(e) => setMinWeight(e.target.value)}
                        required={false}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <FormInput
                        label={isArabic ? 'أقصى وزن (جم)' : 'Max Weight (g)'}
                        type="number"
                        value={maxWeight}
                        onChange={(e) => setMaxWeight(e.target.value)}
                        required={false}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <InputLabel sx={{ fontWeight: 'bold', mb: 1 }}>
                        {isArabic ? 'تفعيل الطريقة' : 'Active'}
                    </InputLabel>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1,
                        border: '1px solid',
                        borderColor: theme.palette.divider,
                        borderRadius: '12px',
                    }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                            {isActive ? (isArabic ? 'نشط' : 'Active') : (isArabic ? 'غير نشط' : 'Inactive')}
                        </Typography>
                        <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />
                    </Box>
                </Grid>
            </Grid>

            <Divider />

            <Box display="flex" justifyContent="flex-end" gap={2}>
                <Box
                    component="button"
                    type="button"
                    onClick={() => onSubmitSuccess && onSubmitSuccess()}
                    disabled={loadingSubmit}
                    sx={{
                        cursor: 'pointer',
                        bgcolor: 'transparent',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '12px',
                        px: 4, py: 1,
                        fontWeight: 'bold',
                        color: 'text.primary',
                    }}
                >
                    {isArabic ? 'إلغاء' : 'Cancel'}
                </Box>
                <Box
                    component="button"
                    type="submit"
                    disabled={loadingSubmit}
                    sx={{
                        cursor: 'pointer',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        border: 'none',
                        borderRadius: '12px',
                        px: 4, py: 1,
                        fontWeight: 'bold',
                        minWidth: 160,
                        boxShadow: theme.shadows[2],
                    }}
                >
                    {loadingSubmit
                        ? (isArabic ? 'جاري الحفظ...' : 'Saving...')
                        : (isEdit ? (isArabic ? 'تحديث' : 'Update') : (isArabic ? 'إنشاء' : 'Create'))}
                </Box>
            </Box>
        </Box>
    );
};

export default FormAddEditShippingMethod;
